// src/storage/mockExamStorage.ts — Bexley Mock Exam Scoring & Persistence (T-12.1.4, FR-MOCK-003)
// Converts raw paper performance to simulated Age-Standardised Scores (SAS)
// and compares against the official Bexley Selection Test qualifying benchmark (216 SAS).

import { MIN_SAS, MAX_SAS } from './readinessEngine';

export interface PaperResult {
  totalQuestions: number;
  correctCount: number;
  unansweredCount: number;
  rawPercent: number;
  sas: number; // 69..141
  timeSpentSeconds: number;
  answers: Record<string, number | null>; // questionId -> optionIndex (0..4) or null
}

export interface DomainScore {
  total: number;
  correct: number;
  percent: number;
}

export interface MockExamRecord {
  id: string;
  timestamp: number;
  dateFormatted: string;
  paper1: PaperResult; // Verbal Ability & English Comprehension
  paper2: PaperResult; // Numerical Reasoning & Non-Verbal/Spatial
  aggregateSas: number; // Paper 1 SAS + Paper 2 SAS
  qualifyingThreshold: number; // 216
  isQualifying: boolean;
  top180Threshold: number; // 234
  isTop180: boolean;
  domainBreakdown: {
    verbal: DomainScore;
    english: DomainScore;
    numerical: DomainScore;
    nvr: DomainScore;
  };
  totalDurationSeconds: number;
}

const STORAGE_KEY = 'clawey_mock_exam_results_v1';

export const BEXLEY_QUALIFYING_THRESHOLD = 216;
export const BEXLEY_TOP_180_THRESHOLD = 234;

export class MockExamStorage {
  /**
   * Convert raw question accuracy into a simulated 11+ Age-Standardised Score (SAS)
   * Calibrated so:
   * 0% -> 69 SAS
   * 50% -> 105 SAS
   * 54% -> 108 SAS (Qualifying benchmark per paper: 108 + 108 = 216)
   * 75% -> 122 SAS
   * 90% -> 133 SAS
   * 100% -> 141 SAS
   */
  static calculatePaperSas(correctCount: number, totalQuestions: number): number {
    if (totalQuestions <= 0) return 100;
    const ratio = Math.max(0, Math.min(1, correctCount / totalQuestions));
    const rawSas = Math.round(69 + ratio * 72);
    return Math.max(MIN_SAS, Math.min(MAX_SAS, rawSas));
  }

  /**
   * Evaluate full mock exam performance and generate an official record
   */
  static evaluateExam(params: {
    paper1Answers: Record<string, number | null>;
    paper1Questions: Array<{ id: string; section: 'verbal' | 'english'; correctOptionIndex: number }>;
    paper1TimeSpentSeconds: number;
    paper2Answers: Record<string, number | null>;
    paper2Questions: Array<{ id: string; section: 'numerical' | 'nvr'; correctOptionIndex: number }>;
    paper2TimeSpentSeconds: number;
  }): MockExamRecord {
    // 1. Evaluate Paper 1
    let p1Correct = 0;
    let p1Unanswered = 0;
    let verbalCorrect = 0;
    let verbalTotal = 0;
    let englishCorrect = 0;
    let englishTotal = 0;

    for (const q of params.paper1Questions) {
      const ans = params.paper1Answers[q.id];
      if (ans === null || ans === undefined) {
        p1Unanswered += 1;
      } else if (ans === q.correctOptionIndex) {
        p1Correct += 1;
        if (q.section === 'verbal') verbalCorrect += 1;
        if (q.section === 'english') englishCorrect += 1;
      }
      if (q.section === 'verbal') verbalTotal += 1;
      if (q.section === 'english') englishTotal += 1;
    }

    const p1Total = params.paper1Questions.length;
    const p1Percent = p1Total > 0 ? Math.round((p1Correct / p1Total) * 100) : 0;
    const p1Sas = this.calculatePaperSas(p1Correct, p1Total);

    const paper1Result: PaperResult = {
      totalQuestions: p1Total,
      correctCount: p1Correct,
      unansweredCount: p1Unanswered,
      rawPercent: p1Percent,
      sas: p1Sas,
      timeSpentSeconds: params.paper1TimeSpentSeconds,
      answers: { ...params.paper1Answers },
    };

    // 2. Evaluate Paper 2
    let p2Correct = 0;
    let p2Unanswered = 0;
    let numCorrect = 0;
    let numTotal = 0;
    let nvrCorrect = 0;
    let nvrTotal = 0;

    for (const q of params.paper2Questions) {
      const ans = params.paper2Answers[q.id];
      if (ans === null || ans === undefined) {
        p2Unanswered += 1;
      } else if (ans === q.correctOptionIndex) {
        p2Correct += 1;
        if (q.section === 'numerical') numCorrect += 1;
        if (q.section === 'nvr') nvrCorrect += 1;
      }
      if (q.section === 'numerical') numTotal += 1;
      if (q.section === 'nvr') nvrTotal += 1;
    }

    const p2Total = params.paper2Questions.length;
    const p2Percent = p2Total > 0 ? Math.round((p2Correct / p2Total) * 100) : 0;
    const p2Sas = this.calculatePaperSas(p2Correct, p2Total);

    const paper2Result: PaperResult = {
      totalQuestions: p2Total,
      correctCount: p2Correct,
      unansweredCount: p2Unanswered,
      rawPercent: p2Percent,
      sas: p2Sas,
      timeSpentSeconds: params.paper2TimeSpentSeconds,
      answers: { ...params.paper2Answers },
    };

    // 3. Aggregate Bexley Standardised Score
    const aggregateSas = p1Sas + p2Sas;
    const isQualifying = aggregateSas >= BEXLEY_QUALIFYING_THRESHOLD;
    const isTop180 = aggregateSas >= BEXLEY_TOP_180_THRESHOLD;

    const now = new Date();
    const dateFormatted = now.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const record: MockExamRecord = {
      id: `mock_${Date.now()}`,
      timestamp: now.getTime(),
      dateFormatted,
      paper1: paper1Result,
      paper2: paper2Result,
      aggregateSas,
      qualifyingThreshold: BEXLEY_QUALIFYING_THRESHOLD,
      isQualifying,
      top180Threshold: BEXLEY_TOP_180_THRESHOLD,
      isTop180,
      domainBreakdown: {
        verbal: {
          total: verbalTotal,
          correct: verbalCorrect,
          percent: verbalTotal > 0 ? Math.round((verbalCorrect / verbalTotal) * 100) : 0,
        },
        english: {
          total: englishTotal,
          correct: englishCorrect,
          percent: englishTotal > 0 ? Math.round((englishCorrect / englishTotal) * 100) : 0,
        },
        numerical: {
          total: numTotal,
          correct: numCorrect,
          percent: numTotal > 0 ? Math.round((numCorrect / numTotal) * 100) : 0,
        },
        nvr: {
          total: nvrTotal,
          correct: nvrCorrect,
          percent: nvrTotal > 0 ? Math.round((nvrCorrect / nvrTotal) * 100) : 0,
        },
      },
      totalDurationSeconds: params.paper1TimeSpentSeconds + params.paper2TimeSpentSeconds,
    };

    this.saveRecord(record);
    return record;
  }

  /** Load all historical mock exam records */
  static loadRecords(): MockExamRecord[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const list = JSON.parse(raw);
      return Array.isArray(list) ? list : [];
    } catch {
      return [];
    }
  }

  /** Alias for loadRecords */
  static getRecords(): MockExamRecord[] {
    return this.loadRecords();
  }

  /** Persist record to historical storage */
  static saveRecord(record: MockExamRecord): void {
    try {
      const list = this.loadRecords();
      list.unshift(record);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch {
      // Ignore quota exceptions
    }
  }

  /** Retrieve the most recent completed mock exam record */
  static getLatestRecord(): MockExamRecord | null {
    const list = this.loadRecords();
    return list[0] ?? null;
  }

  /** Clear all mock records (test helper) */
  static clear(): void {
    localStorage.removeItem(STORAGE_KEY);
  }

  /** Alias for clear */
  static clearRecords(): void {
    this.clear();
  }
}
